// ---------------------------------------------------------------------------
// Steps: the tool calls Copilot made for an answer, shown under it as a list.
//
// The stream carries them as `{"event": {"type": "tool", "phase": "start" | "end", ...}}`:
//   start: {id, name, label}        end: {id, status: "success" | "error"}  (the event also carries `secs`; the list shows no times)
// Only labels and timings travel, never what went into or came out of a tool.
// ---------------------------------------------------------------------------

export const STEP_RUNNING = 'running';
export const STEP_DONE = 'done';
export const STEP_ERROR = 'error';
export const STEP_THOUGHT = 'thought';

/** Whether the step is a piece of the model's reasoning (`{kind: 'thought', text}`), not a tool call. */
export const isThought = (step) => step?.kind === STEP_THOUGHT;

/** `steps` with the event applied. The same array when the event is not a step event, so callers can compare. */
export function applyStepEvent(steps, event) {
  if (event?.type === 'thought') {
    const text = String(event.text ?? '');
    if (!text) return steps;
    const last = steps[steps.length - 1];
    if (isThought(last)) return [...steps.slice(0, -1), { ...last, text: last.text + text }]; // deltas of one thought
    return [...steps, { id: `thought-${steps.length}`, kind: STEP_THOUGHT, text, label: '', status: STEP_DONE }];
  }
  if (!event || event.type !== 'tool' || !event.id) return steps;
  if (event.phase === 'start') {
    const step = { id: event.id, name: event.name ?? '', label: event.label || event.name || '', status: STEP_RUNNING };
    return [...steps.filter((s) => s.id !== event.id), step];
  }
  if (event.phase === 'end') {
    return steps.map((s) =>
      s.id === event.id
        ? { ...s, status: event.status === 'success' ? STEP_DONE : STEP_ERROR }
        : s,
    );
  }
  return steps;
}

/** The step that is running now (the latest one), or null. */
export function runningStep(steps) {
  const running = (steps ?? []).filter((s) => !isThought(s) && s.status === STEP_RUNNING);
  return running.length ? running[running.length - 1] : null;
}

/** The tool steps: what the "Steps (N)" count and the seconds are about. */
export const toolSteps = (steps) => (steps ?? []).filter((s) => !isThought(s));

/** What the "Steps (N)" count is about: every line the list shows, the model's own notes (narration, headings of its reasoning) as well as
 * the tool calls, so the number matches what the user can count. */
export const stepCount = (steps) => (steps ?? []).filter((s) => !isThought(s) || thoughtText(s.text)).length;

/** `steps` with the running ones marked done (no time): used when the run paused for an approval and the tool never ended. */
export function finishRunning(steps) {
  if (!steps.some((s) => s.status === STEP_RUNNING)) return steps;
  return steps.map((s) => (s.status === STEP_RUNNING ? { ...s, status: STEP_DONE } : s));
}

/** The steps as rows: consecutive steps with the same label become one row `{id, label, status, count, failed}` (14 searches read as one
 * line with ×14; `failed` says how many of them failed, so a retry that worked is not shown as a failure of all). */
export function groupSteps(steps) {
  const rows = [];
  (steps ?? []).forEach((step) => {
    if (isThought(step)) {
      if (!thoughtText(step.text)) return; // nothing to show yet, or no heading
      rows.push({ id: step.id, kind: STEP_THOUGHT, text: step.text, label: '', status: STEP_DONE, count: 1, failed: 0 });
      return;
    }
    const failed = step.status === STEP_ERROR ? 1 : 0;
    const last = rows[rows.length - 1];
    if (last && !isThought(last) && last.label === step.label) {
      last.count += 1;
      last.failed += failed;
      if (step.status !== STEP_RUNNING && last.status !== STEP_RUNNING) last.status = step.status; // the latest try decides: a retry that worked is done
      else if (step.status === STEP_RUNNING) last.status = STEP_RUNNING;
      return;
    }
    rows.push({ id: step.id, label: step.label, status: step.status, count: 1, failed });
  });
  return rows;
}

/** What a thought shows: only its headings (`**Gathering story summaries**`), one per line. The text under a heading is the model's working
 * notes (tool names, SQL, Python): it is not for the user. A heading still being streamed shows as far as it has come. '' when there is none. */
export const thoughtText = (text) =>
  [...String(text ?? '').matchAll(/\*\*([^*]+)(\*\*|$)/g)]
    .map((m) => m[1].trim())
    .filter(Boolean)
    .join('\n');

/** What Copilot is doing now, one label for the loader and the list: the running tool, else the tool the model has just started to call
 * (`custom.status`), else null (the model is thinking). */
export function activityLabel(custom) {
  return runningStep(custom?.steps)?.label ?? custom?.status ?? null;
}

/** The label of a `status` event: its text without the trailing dots, or null. */
export function statusLabel(event) {
  const text = String(event?.text ?? '').replace(/\.+$/, '').trim();
  return text || null;
}

/** Narration text (streamed before a tool call) as a thought, i.e. a single heading line for the Steps list. */
export const narrationAsThought = (text) => {
  const line = String(text ?? '').replace(/\*/g, '').replace(/\s+/g, ' ').trim();
  return line ? `**${line}**` : '';
};

/** `steps` with the narration a model turn wrote before its tool calls ("I'll check...") added as a thought; the same array when there is none. */
export function addNarration(steps, narration) {
  const text = narrationAsThought(narration);
  return text ? applyStepEvent(steps, { type: 'thought', text }) : steps;
}
